import { UserFriendlyError } from '@affine/error';
import type { useI18n } from '@affine/i18n';

import { DurableWriteSyncError } from '../../doc/services/database-system-doc-access';
import { appBaseErrorData, hasAppBaseErrorCode } from './app-base-error-code';
import { appBasePlanRefusalMessage } from './app-base-plan-pause';

/** Server code of a save or publish the App Container is not complete for. */
const APP_BASE_CONTAINER_INCOMPLETE = 'APP_BASE_CONTAINER_INCOMPLETE';
/** Server code of a publish an automation of an App could not be switched on for. */
const APP_BASE_PUBLISH_AUTOMATION_REFUSED =
  'APP_BASE_PUBLISH_AUTOMATION_REFUSED';

export type AppBaseVersioningAction =
  | 'load'
  | 'save'
  | 'publish'
  | 'unpublish'
  | 'discard'
  | 'restore';

type I18n = ReturnType<typeof useI18n>;

const PERMISSION_ERROR_NAMES = new Set([
  'ACCESS_DENIED',
  'ACTION_FORBIDDEN',
  'AUTHENTICATION_REQUIRED',
  'SPACE_ACCESS_DENIED',
]);

function actionTitle(t: I18n, action: AppBaseVersioningAction): string {
  switch (action) {
    case 'load':
      return t['com.streamlain.appBases.versioning.error.load']();
    case 'save':
      return t['com.streamlain.appBases.versioning.error.save']();
    case 'publish':
      return t['com.streamlain.appBases.versioning.error.publish']();
    case 'unpublish':
      return t['com.streamlain.appBases.versioning.error.unpublish']();
    case 'discard':
      return t['com.streamlain.appBases.versioning.error.discard']();
    case 'restore':
      return t['com.streamlain.appBases.versioning.error.restore']();
  }
}

/** The automation of the App the publish could not switch on, and why. */
function automationRefusalMessage(
  t: I18n,
  data: Record<string, unknown>
): string {
  const automation = String(data.automationKey);
  const reason = data.reason;
  if (reason === 'AUTOMATION_TOPOLOGY_BLOCKED_CYCLE') {
    return t['com.streamlain.appBases.versioning.error.cause.automationLoop']({
      automation,
    });
  }
  if (reason === 'input.source-rowless') {
    return t[
      'com.streamlain.appBases.versioning.error.cause.automationRowless'
    ]({ automation });
  }
  if (reason === 'AUTOMATION_SETUP_MISSING') {
    return t['com.streamlain.appBases.versioning.error.cause.automationSetup']({
      automation,
    });
  }
  return t['com.streamlain.appBases.versioning.error.cause.automationRefused']({
    automation,
  });
}

function causeMessage(t: I18n, error: unknown): string {
  if (error instanceof DurableWriteSyncError) {
    return t['com.streamlain.appBases.versioning.error.cause.sync']();
  }
  const planRefusal = appBasePlanRefusalMessage(t, error);
  if (planRefusal) return planRefusal;
  if (hasAppBaseErrorCode(error, APP_BASE_CONTAINER_INCOMPLETE)) {
    return t['com.streamlain.appBases.versioning.error.cause.incomplete']();
  }
  const refusedAutomation = appBaseErrorData(
    error,
    APP_BASE_PUBLISH_AUTOMATION_REFUSED
  );
  if (refusedAutomation) {
    return automationRefusalMessage(t, refusedAutomation);
  }
  const friendly = UserFriendlyError.fromAny(error);
  if (friendly.isNetworkError()) {
    return t['com.streamlain.appBases.versioning.error.cause.network']();
  }
  if (PERMISSION_ERROR_NAMES.has(friendly.name)) {
    return t['com.streamlain.appBases.versioning.error.cause.permission']();
  }
  // Server-side UserFriendlyErrors carry a message written for end users;
  // anything else is an internal detail and stays in the console.
  if (friendly.name !== 'INTERNAL_SERVER_ERROR' && friendly.message) {
    return friendly.message;
  }
  return t['com.streamlain.appBases.versioning.error.cause.unknown']();
}

/**
 * Maps a draft/publish lifecycle failure to a toast that tells the user what
 * failed and what to do next, without leaking document ids or stack details.
 */
export function describeAppBaseVersioningError(
  t: I18n,
  action: AppBaseVersioningAction,
  error: unknown
): { title: string; message: string } {
  return { title: actionTitle(t, action), message: causeMessage(t, error) };
}
